import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Button, Form, Table, Card, Badge } from 'react-bootstrap';
import { InvestorVehicleModal } from './InvestorVehicleModal';

/**
 * UserList component displays all users (customers and agents) in a table
 * and provides navigation to detail view for editing permissions
 */
export function UserList() {
  const navigate = useNavigate();
  const { apiCall, user } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assigningInvestor, setAssigningInvestor] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [roleFilter, apiCall]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = roleFilter ? `?role=${roleFilter}` : '';
      const response = await apiCall(`${API_BASE_URL}/users${params}`);
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'manager':
        return 'primary';
      case 'auditor':
        return 'info';
      case 'investor':
        return 'success';
      case 'agent':
        return 'warning';
      case 'customer':
        return 'secondary';
      default:
        return 'dark';
    }
  };

  const formatRole = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2>User Management</h2>
        </Col>
        {user?.role === 'admin' && (
          <Col xs="auto">
            <Button variant="primary" onClick={() => navigate('./create')}>
              + Create Agent
            </Button>
          </Col>
        )}
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger">{error}</div>
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={3}>
          <Form.Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            disabled={loading}
          >
            <option value="">All Users</option>
            <option value="admin">Admins</option>
            <option value="manager">Managers</option>
            <option value="auditor">Auditors</option>
            <option value="investor">Investors</option>
            <option value="agent">Agents</option>
            <option value="customer">Customers</option>
          </Form.Select>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No users found.
            </div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Store</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((userItem) => (
                  <tr key={`${userItem.role}-${userItem.id}`}>
                    <td>{userItem.id}</td>
                    <td>{userItem.name}</td>
                    <td>{userItem.email || '-'}</td>
                    <td>
                      <Badge bg={getRoleBadgeVariant(userItem.role)}>
                        {formatRole(userItem.role)}
                      </Badge>
                    </td>
                    <td>{userItem.store_name || '-'}</td>
                    <td>
                      {userItem.role !== 'customer' ? (
                        <div className="d-flex gap-2">
                          {userItem.can_delete && (
                            <Badge bg="success" className="small">Delete</Badge>
                          )}
                          {userItem.view_store_ids && userItem.view_store_ids.length > 1 && (
                            <Badge bg="info" className="small">
                              {userItem.view_store_ids.length} stores
                            </Badge>
                          )}
                          {userItem.role === 'auditor' && (
                            <Badge bg="warning" className="small">Read-Only</Badge>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(`./${userItem.id}?role=${userItem.role}`)}
                      >
                        View/Edit
                      </Button>
                      {userItem.role === 'investor' && user?.role === 'admin' && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => setAssigningInvestor(userItem)}
                        >
                          Assign Vehicles
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {users.length > 0 && (
        <Row className="mt-3">
          <Col className="text-muted">
            Showing {users.length} {roleFilter ? formatRole(roleFilter) + 's' : 'users'}
          </Col>
        </Row>
      )}

      <InvestorVehicleModal
        investor={assigningInvestor}
        show={!!assigningInvestor}
        onHide={() => setAssigningInvestor(null)}
        apiCall={apiCall}
      />
    </Container>
  );
}
